/* eslint-disable react-refresh/only-export-components */
import {
  ChevronDownIcon,
  SearchIcon,
  UsersIcon,
  XIcon,
  XSquareIcon,
} from '@/icons';
import { formatDate } from '@/utils';
import {
  Button,
  Checkbox,
  Collapse,
  Flex,
  Form,
  Input,
  Select,
  type FormInstance,
} from 'antd';
import { useMemo, useState } from 'react';
import IMAGES from '../assets/images';
import CustomImage from './CustomImage';
import SharedEmptyState from './EmptyState';
import WithLoader from './WithLoader';

/**
 * Minimal interface representing an employee for selection purposes.
 */
export interface EmployeeMinimal {
  id: string;
  user_id: string;
  employee_id: string;
  name: string;
  photo?: string | null;
  disbursed_count?: number;
  department?: {
    id: string;
    name: string;
  } | null;
  designation?: {
    id: string;
    name: string;
  } | null;
  joining_date?: string;
}

const { Panel } = Collapse;

/**
 * Utilities for employee data transformation and grouping inside EmployeeSelect and its parents.
 */
export const employeeUtils = {
  /**
   * Groups employees by their department name.
   */
  groupByDepartment: (employees: EmployeeMinimal[]) => {
    if (!employees?.length) return {};
    return employees.reduce((acc: Record<string, EmployeeMinimal[]>, emp) => {
      const deptName = emp?.department?.name || 'No Department';
      if (!acc[deptName]) acc[deptName] = [];
      acc[deptName].push(emp);
      return acc;
    }, {});
  },

  /**
   * Filters employees based on a search keyword (Name or ID).
   */
  filterEmployees: (employees: EmployeeMinimal[], keyword: string) => {
    if (!keyword) return employees;
    const lowerKeyword = keyword?.toLowerCase();
    return employees?.filter(
      (emp) =>
        emp?.name?.toLowerCase()?.includes(lowerKeyword) ||
        emp?.employee_id?.toLowerCase()?.includes(lowerKeyword),
    );
  },

  /**
   * Transforms raw selected employees (grouped by dept) into a grouped list for display.
   * This is particularly useful for rendering selected cards in the UI.
   */
  getGroupedSelectedEmployees: (
    employees: EmployeeMinimal[],
    assignedEmployees: Record<string, string[]>,
    valueKey: keyof EmployeeMinimal = 'user_id',
  ) => {
    if (!employees?.length || !assignedEmployees) return {};
    const selectedUserIds = Object.values(assignedEmployees).flat();

    return employees.reduce((acc: Record<string, EmployeeMinimal[]>, emp) => {
      const deptName = emp?.department?.name || 'No Department';
      if (selectedUserIds.includes(emp[valueKey] as string)) {
        if (!acc[deptName]) acc[deptName] = [];
        acc[deptName].push(emp);
      }
      return acc;
    }, {});
  },
};

type PopupProps = {
  employees: EmployeeMinimal[];
  closeOpen: () => void;
  showJoinDate?: boolean;
  valueKey?: keyof EmployeeMinimal;
};

const EmployeePopup = ({
  employees,
  closeOpen,
  showJoinDate,
  valueKey = 'user_id',
}: PopupProps) => {
  const form = Form.useFormInstance();
  const selectedEmployees = Form.useWatch('selected_employees', form);
  const [keyword, setKeyword] = useState('');

  // Filter employees based on keyword locally
  const filteredEmployees = useMemo(
    () => employeeUtils.filterEmployees(employees, keyword),
    [employees, keyword],
  );

  // Group employees by department
  const employeesByDepartment = useMemo(
    () => employeeUtils.groupByDepartment(filteredEmployees),
    [filteredEmployees],
  );

  const departmentKeys = useMemo(
    () => Object.keys(employeesByDepartment),
    [employeesByDepartment],
  );

  // Select all employees from all departments
  const handleSelectAllDepartments = (checked: boolean) => {
    if (checked) {
      const allSelected: Record<string, string[]> = {};
      Object.entries(employeesByDepartment).forEach(([dept, emps]) => {
        allSelected[dept] = emps.map((e) => e[valueKey] as string);
      });
      form.resetFields(['selected_employees']);
      form.setFieldsValue({ selected_employees: allSelected });
    } else {
      form.resetFields(['selected_employees']);
      form.setFieldsValue({ selected_employees: {} });
    }
  };

  const handleDepartmentChange = (dept: string, checked: boolean) => {
    const newSelected = { ...selectedEmployees };
    if (checked) {
      newSelected[dept] = employeesByDepartment[dept].map(
        (e) => e[valueKey] as string,
      );
    } else {
      delete newSelected[dept];
    }
    form.resetFields(['selected_employees']);
    form.setFieldsValue({ selected_employees: newSelected });
  };

  const handleEmployeeChange = (dept: string, id: string, checked: boolean) => {
    const newSelected = { ...selectedEmployees };
    const deptSelected = newSelected[dept] || [];

    if (checked) {
      newSelected[dept] = [...deptSelected, id];
    } else {
      newSelected[dept] = deptSelected.filter((empId: string) => empId !== id);
      if (newSelected[dept].length === 0) delete newSelected[dept];
    }
    form.resetFields(['selected_employees']);
    form.setFieldsValue({ selected_employees: newSelected });
  };

  const isEmployeeSelected = (dept: string, id: string) =>
    (selectedEmployees?.[dept] || []).includes(id);

  const isDepartmentSelected = (dept: string) => {
    const deptEmployees = employeesByDepartment[dept] || [];
    const selectedDept = selectedEmployees?.[dept] || [];
    return (
      deptEmployees.length > 0 &&
      deptEmployees.every((e) => selectedDept.includes(e[valueKey] as string))
    );
  };

  const isAllDepartmentsSelected = () =>
    departmentKeys.length > 0 && departmentKeys.every(isDepartmentSelected);

  const handleAssign = () => {
    form.setFieldValue('assigned_employees', selectedEmployees);
    closeOpen();
  };

  return (
    <div className="w-full input-bg-transparent">
      <Input
        onChange={(e) => setKeyword(e.target.value)}
        value={keyword}
        placeholder="Search Employee ID, Name"
        prefix={<SearchIcon size={20} />}
        className="mb-4"
      />

      <div className="flex items-center gap-3 border-b border-b-theme-gray-700 mb-3 pb-3">
        <Checkbox
          checked={isAllDepartmentsSelected()}
          onChange={(e) => handleSelectAllDepartments(e.target.checked)}
        />
        <div className="text-white text-base font-bold">
          Select all Departments
        </div>
      </div>

      <div className="max-h-[250px] overflow-y-auto custom-scroll pr-2">
        {departmentKeys.length === 0 ? (
          <SharedEmptyState
            className="py-5"
            icon={<UsersIcon size={36} />}
            title="No Employees Found"
            description="Try adjusting your search or select a different month/year."
          />
        ) : (
          <Collapse
            bordered={false}
            accordion={false}
            defaultActiveKey={departmentKeys.slice(0, 1)}
            expandIconPosition="right"
            className="custom-collapse"
          >
            {departmentKeys.map((dept, idx) => (
              <Panel
                key={idx}
                header={
                  <div className="flex items-center gap-3">
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isDepartmentSelected(dept)}
                        onChange={(e) =>
                          handleDepartmentChange(dept, e.target.checked)
                        }
                      />
                    </div>
                    <div className="text-white text-base font-bold">{dept}</div>
                  </div>
                }
                className="!rounded-none !border-b !border-b-theme-gray-700"
              >
                <span className="text-sm text-theme-primary font-medium block mb-3">
                  Total Employee
                  {employeesByDepartment[dept].length > 1 ? 's' : ''} (
                  {employeesByDepartment[dept].length})
                </span>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {employeesByDepartment[dept].map((emp) => (
                    <Flex
                      key={emp.id}
                      align="center"
                      gap={10}
                      className="w-full overflow-hidden"
                    >
                      <Checkbox
                        checked={isEmployeeSelected(
                          dept,
                          emp[valueKey] as string,
                        )}
                        onChange={(e) =>
                          handleEmployeeChange(
                            dept,
                            emp[valueKey] as string,
                            e.target.checked,
                          )
                        }
                      />
                      <CustomImage
                        width={25}
                        height={25}
                        src={emp.photo || IMAGES.avatar}
                        className="rounded-full flex-shrink-0"
                      />
                      <div className="overflow-hidden min-w-0">
                        <h5 className="text-sm font-medium text-white truncate">
                          {emp.name}
                        </h5>
                        <span className="text-xs block text-white/70">
                          ID: {emp.employee_id}
                        </span>
                        {showJoinDate && emp.joining_date && (
                          <span className="text-xs block text-white/50 font-light">
                            {formatDate(emp.joining_date)}
                          </span>
                        )}
                      </div>
                    </Flex>
                  ))}
                </div>
              </Panel>
            ))}
          </Collapse>
        )}
      </div>

      <Flex justify="end" className="mt-6 pt-4 border-t border-theme-gray-700">
        <Button
          htmlType="button"
          onClick={handleAssign}
          className="btn-primary min-w-[100px]"
        >
          Assign
        </Button>
      </Flex>
    </div>
  );
};

type Props = {
  form: FormInstance<any>;
  employees: EmployeeMinimal[];
  label?: string;
  placeholder?: string;
  showJoinDate?: boolean;
  required?: boolean;
  valueKey?: keyof EmployeeMinimal;
};

/**
 * Reusable Employee Selection Component
 * Allows searching and selecting employees by department.
 */
const EmployeeSelect = ({
  form,
  employees = [],
  label = 'Select Employee (s)',
  placeholder = 'Select',
  showJoinDate = false,
  required = true,
  valueKey = 'user_id',
}: Props) => {
  const [open, setOpen] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      const current = form.getFieldValue('assigned_employees') || {};
      form.setFieldValue('selected_employees', current);
    }
  };

  return (
    <>
      <Form.Item initialValue={{}} noStyle name="selected_employees" />

      <Form.Item
        name="assigned_employees"
        label={label}
        rules={[{ required, message: 'Please select employees' }]}
        // The value is `{ [department]: ids[] }`; the box shows how many are
        // picked, or the placeholder when none are.
        getValueProps={(value?: Record<string, string[]>) => {
          const count = Object.values(value || {}).flat().length;
          return {
            value: count
              ? `${count} employee${count === 1 ? '' : 's'} selected`
              : undefined,
          };
        }}
      >
        <Select
          open={open}
          placeholder={placeholder}
          onOpenChange={handleOpenChange}
          styles={{ popup: { root: { minWidth: 600 } } }}
          classNames={{
            popup: {
              root: 'bg-theme-slate-800 p-4 border border-theme-slate-700 rounded-lg shadow-2xl overflow-hidden',
            },
          }}
          suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
          popupRender={() => (
            <EmployeePopup
              employees={employees}
              closeOpen={() => setOpen(false)}
              showJoinDate={showJoinDate}
              valueKey={valueKey}
            />
          )}
        />
      </Form.Item>
    </>
  );
};

type SelectedProps = {
  form: FormInstance<any>;
  employees: EmployeeMinimal[];
  assignedEmployees: Record<string, string[]>;
  isView?: boolean;
  fieldName?: string;
  renderExtraInfo?: (user: any) => React.ReactNode;
  valueKey?: keyof EmployeeMinimal;
  allowRemoveEmployee?: boolean;
};

const Selected = ({
  form,
  employees,
  assignedEmployees,
  isView = false,
  fieldName = 'assigned_employees',
  renderExtraInfo,
  valueKey = 'user_id',
  allowRemoveEmployee = false,
}: SelectedProps) => {
  const employeesByDepartment = useMemo(() => {
    return employeeUtils.getGroupedSelectedEmployees(
      employees || [],
      assignedEmployees,
      valueKey,
    );
  }, [employees, assignedEmployees, valueKey]);

  const departmentKeys = Object.keys(employeesByDepartment);

  const removeDepartment = (dept: string) => {
    const newSelected = { ...assignedEmployees };
    delete newSelected[dept];
    form.resetFields([fieldName]);
    form.setFieldsValue({ [fieldName]: newSelected });
  };

  const removeEmployee = (dept: string, empValue: string) => {
    const newSelected = { ...assignedEmployees };
    const remaining = (newSelected[dept] || []).filter((v) => v !== empValue);
    if (remaining.length === 0) {
      delete newSelected[dept];
    } else {
      newSelected[dept] = remaining;
    }
    form.resetFields([fieldName]);
    form.setFieldsValue({ [fieldName]: newSelected });
  };

  return (
    <div className="space-y-3">
      <h6 className="text-base text-white/60 font-medium border-b border-theme-gray-700 pb-3">
        Selected Employee (s)
      </h6>

      {departmentKeys?.length ? (
        departmentKeys?.map((dept) => (
          <div key={dept}>
            <Flex
              align="center"
              justify="space-between"
              className="border-b border-b-theme-slate-700 border-dashed pb-3"
            >
              <h6 className="text-base text-white font-medium">{dept}</h6>
              <button
                type="button"
                disabled={isView}
                onClick={() => !isView && removeDepartment(dept)}
              >
                <XSquareIcon size={20} />
              </button>
            </Flex>
            <WithLoader isLoading={false}>
              {employeesByDepartment[dept]?.length > 0 ? (
                <div className="my-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                  {employeesByDepartment[dept]?.map((user) => (
                    <Flex
                      key={user?.id}
                      gap={10}
                      className="p-2.5 rounded-lg bg-theme-slate-800 relative"
                    >
                      {/* Single employee remove button — shown only when allowRemoveEmployee=true */}
                      {allowRemoveEmployee && !isView && (
                        <button
                          type="button"
                          onClick={() =>
                            removeEmployee(dept, user[valueKey] as string)
                          }
                          className="absolute -right-1 -top-1 p-0.5 bg-theme-primary rounded-full"
                        >
                          <XIcon size={12} strokeWidth={3} />
                        </button>
                      )}
                      <CustomImage
                        alt="User"
                        width={25}
                        height={25}
                        src={user?.photo || IMAGES.avatar}
                        className="rounded-full flex-shrink-0"
                      />
                      <div className="overflow-hidden min-w-0 flex-1">
                        <span className="block text-xs text-white/70">
                          ID #{user?.employee_id}
                        </span>
                        <span className="block text-base font-medium truncate">
                          {user?.name}
                        </span>
                        {renderExtraInfo ? (
                          renderExtraInfo(user)
                        ) : (
                          <span className="block text-xs font-medium text-theme-secondary/80 truncate">
                            {user?.designation?.name}
                          </span>
                        )}
                      </div>
                    </Flex>
                  ))}
                </div>
              ) : (
                <SharedEmptyState
                  className="py-5"
                  icon={<UsersIcon size={36} />}
                  title="No Employees Found"
                  description="There are no employees assigned to this department yet."
                />
              )}
            </WithLoader>
          </div>
        ))
      ) : (
        <SharedEmptyState
          className="py-8"
          icon={<UsersIcon size={36} />}
          title="No Employees Selected"
          description="Employees picked from the list above appear here."
        />
      )}
    </div>
  );
};

EmployeeSelect.Selected = Selected;

export default EmployeeSelect;
